import { ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { StatusBadge, type Tom } from "@/components/data/status-badge";
import { DataTableRow, DataTableCell } from "@/components/data/data-table";
import type { LinhaMensalistaMes, QuinzenaDaCompetencia } from "@/hooks/use-equipe";
import {
  formatarBRL, formatarDataCurta, paraReais, paraCentavos,
  estadoLinhaMes, situacaoAdiantamentoMes, salarioLinhaMes, liquidoLinhaMes,
  adiantamentoLinhaMesCentavos, calcAvisoTetoVariavel, formatarAvisoTetoVariavel,
  agruparLancamentosPorPagamento, textoPagamentoLancamentos,
  type EstadoLinhaMes, type SituacaoAdiantamentoMes,
} from "@shared/equipe-fechamento";

/** "Quinzenal" / "1×/mês" / "Encarregado · A1, A2" — o chip de regime. */
export function chipRegime(l: LinhaMensalistaMes): { texto: string; tom: Tom } {
  if (l.era_mensalista) return { texto: "era mensalista", tom: "atencao" };
  if (l.encarregado) {
    const setores = l.setores_encarregado;
    const rotulo = setores.length === 0 ? "" : setores.length <= 3 ? ` · ${setores.join(", ")}` : ` · ${setores[0]} +${setores.length - 1}`;
    return { texto: `Encarregado${rotulo}`, tom: "info" };
  }
  return (l.forma_pagamento ?? "MENSAL") === "QUINZENAL"
    ? { texto: "Quinzenal", tom: "neutro" }
    : { texto: "1×/mês", tom: "neutro" };
}

export const ESTADO_LINHA: Record<EstadoLinhaMes, { texto: string; tom: Tom }> = {
  projecao: { texto: "projeção", tom: "neutro" },
  aberto: { texto: "a pagar", tom: "atencao" },
  fechado: { texto: "pago", tom: "sucesso" },
};

/** Sublinha da coluna Adiantamento — sempre diz COM QUAL quinzena / como foi pago. */
export function sublinhaAdiantamento(l: LinhaMensalistaMes, situacao: SituacaoAdiantamentoMes, quinzena: QuinzenaDaCompetencia | null | undefined): string {
  const a = l.adiantamento;
  const dataDaQuinzena = a ? (a.quinzena_data_pagamento ?? a.quinzena_periodo_fim) : (quinzena?.dataPagamento ?? quinzena?.periodoFim ?? null);
  switch (situacao) {
    case "pago_quinzena": return `pago com a quinzena de ${formatarDataCurta(dataDaQuinzena)}`;
    case "previsto": return `previsto · quinzena de ${formatarDataCurta(dataDaQuinzena)}`;
    case "pago_fora": return `pago por fora em ${formatarDataCurta(a?.pago_em)}`;
    case "dispensado": return `não houve · ${l.dispensa?.motivo ?? ""}`;
    case "a_gerar": return "a gerar";
    case "mensal": return "1×/mês";
    default: return "";
  }
}

/** "21 ✓ · 1 falta" */
export function PresencaResumo({ l }: { l: LinhaMensalistaMes }) {
  return (
    <span className="text-xs whitespace-nowrap">
      <span className="font-semibold text-sucesso">{l.dias_registro} ✓</span>
      {l.dias_falta > 0 && (
        <>
          <span className="text-muted-foreground"> · </span>
          <span className="font-semibold text-atencao">{l.dias_falta} falta{l.dias_falta === 1 ? "" : "s"}</span>
        </>
      )}
    </span>
  );
}

/**
 * As DUAS últimas colunas (Estado e a ação "Pago") GRUDAM à direita.
 *
 * Em 1440 px a tabela rolava 107 px na horizontal e o que ficava de fora era
 * justamente a coluna do botão — com o chip de estado cortado ao meio
 * ("a paga…"). No aperto esconde-se METADADO, nunca IDENTIDADE nem AÇÃO.
 *
 * Fundo em DUAS camadas, de propósito: `bg-card` sólido (senão o conteúdo que
 * passa por baixo aparece através da célula fixa) mais um `::before`
 * translúcido que reproduz o `hover:bg-muted/40` da linha, para a faixa fixa
 * não destoar do resto dela. O conteúdo vai em `relative z-10`, acima do
 * overlay. Largura fixa porque a coluna de Estado se ancora na da ação
 * (`right-[92px]`) — as duas precisam concordar.
 */
const FIXA_BASE = "sticky z-20 bg-card before:absolute before:inset-0 before:content-[''] before:pointer-events-none";
export const COL_ESTADO_FIXA = `${FIXA_BASE} right-[92px] w-[112px] min-w-[112px] shadow-[-10px_0_10px_-10px_rgba(0,0,0,0.18)]`;
export const COL_ACAO_FIXA = `${FIXA_BASE} right-0 w-[92px] min-w-[92px]`;
/** Nas LINHAS o overlay acompanha o hover; no cabeçalho e no TOTAL ele é fixo. */
export const FIXA_HOVER = "group-hover:before:bg-muted/40";
export const FIXA_CABECALHO = "before:bg-muted/40";
export const FIXA_TOTAL = "before:bg-muted/50";

export interface LinhaMensalistaMesProps {
  l: LinhaMensalistaMes;
  quinzena: QuinzenaDaCompetencia | null | undefined;
  isAdmin: boolean;
  onAbrir: (l: LinhaMensalistaMes) => void;
  /** "Pago" — só ABERTO e admin. */
  onPago: (l: LinhaMensalistaMes) => void;
  pagando: boolean;
  /** Está na lista de pendências do servidor — o "Pago" sai desabilitado. */
  pendente: boolean;
}

/** Uma linha da tabela desktop (§4.5). Célula de presença NUNCA mostra R$. */
export function LinhaMensalistaMesDesktop({ l, quinzena, isAdmin, onAbrir, onPago, pagando, pendente }: LinhaMensalistaMesProps) {
  const estado = estadoLinhaMes(l);
  const situacao = situacaoAdiantamentoMes(l);
  const chip = chipRegime(l);
  const adiantado = adiantamentoLinhaMesCentavos(l);
  const primeiroDesconto = l.lancamentos.find(x => x.sinal === "DESCONTO");
  // QUANDO os adicionais saíram, pela quinzena que os carrega — não pela data
  // em que foram lançados, e sem chamar de "pago" o que ainda vai sair.
  const pagamentoAdicionais = textoPagamentoLancamentos(
    agruparLancamentosPorPagamento(l.lancamentos.filter(x => x.sinal !== "DESCONTO")),
  );
  const avisoTeto = calcAvisoTetoVariavel({
    variavelApurada: l.fechamento?.variavel_apurada ?? 0,
    adicionalCentavos: l.adicionais_centavos - l.auxilios_centavos,
    variavelMaxMensal: l.variavel_max_mensal,
  });

  return (
    <DataTableRow className="group cursor-pointer hover:bg-muted/40" onClick={() => onAbrir(l)} data-testid={`linha-mes-${l.id}`}>
      <DataTableCell className="font-medium text-sm">
        <div className="flex items-center gap-1.5 min-w-0">
          <span className="truncate">{l.apelido ?? l.nome}</span>
          <StatusBadge tom={chip.tom} className="text-[10px] font-normal shrink-0">{chip.texto}</StatusBadge>
        </div>
        {avisoTeto?.estourou && (
          <p className="text-[10px] text-atencao mt-0.5">{formatarAvisoTetoVariavel(avisoTeto)}</p>
        )}
      </DataTableCell>
      <DataTableCell className="text-center"><PresencaResumo l={l} /></DataTableCell>
      <DataTableCell className="text-right font-mono text-xs tabular-nums">{formatarBRL(salarioLinhaMes(l))}</DataTableCell>
      <DataTableCell className="text-right font-mono text-xs tabular-nums">
        {adiantado > 0
          ? <span className="text-erro font-medium">− {formatarBRL(paraReais(adiantado))}</span>
          : <span className="text-muted-foreground">—</span>}
        <span className={`block text-[10px] font-sans ${situacao === "a_gerar" ? "text-erro font-semibold" : "text-muted-foreground"}`}>
          {sublinhaAdiantamento(l, situacao, quinzena)}
        </span>
      </DataTableCell>
      <DataTableCell className="text-right font-mono text-xs tabular-nums">
        {l.adicionais_centavos > 0
          ? <span className="text-sucesso font-medium">+ {formatarBRL(paraReais(l.adicionais_centavos))}</span>
          : <span className="text-muted-foreground">—</span>}
        {pagamentoAdicionais && (
          <span className="block text-[10px] font-sans text-muted-foreground">{pagamentoAdicionais}</span>
        )}
      </DataTableCell>
      <DataTableCell className="text-right font-mono text-xs tabular-nums">
        {l.descontos_centavos > 0
          ? <span className="text-erro font-medium">− {formatarBRL(paraReais(l.descontos_centavos))}</span>
          : <span className="text-muted-foreground">—</span>}
        {primeiroDesconto && (
          <span className="block text-[10px] font-sans text-muted-foreground truncate max-w-[140px]">{primeiroDesconto.descricao ?? "desconto"}</span>
        )}
      </DataTableCell>
      <DataTableCell className="text-right font-mono text-sm font-bold tabular-nums">{formatarBRL(liquidoLinhaMes(l))}</DataTableCell>
      <DataTableCell className={`text-center ${COL_ESTADO_FIXA} ${FIXA_HOVER}`}>
        <span className="relative z-10">
          <StatusBadge tom={ESTADO_LINHA[estado].tom} ponto>{ESTADO_LINHA[estado].texto}</StatusBadge>
        </span>
      </DataTableCell>
      <DataTableCell className={`text-right ${COL_ACAO_FIXA} ${FIXA_HOVER}`}>
        {isAdmin && estado === "aberto" && (
          <Button
            size="sm"
            className="relative z-10 h-7 text-xs bg-sucesso hover:bg-sucesso/90 text-white"
            disabled={pagando || pendente}
            title={pendente ? "Resolva a pendência de adiantamento antes." : undefined}
            onClick={e => { e.stopPropagation(); onPago(l); }}
            data-testid={`pago-${l.id}`}
          >
            Pago
          </Button>
        )}
      </DataTableCell>
    </DataTableRow>
  );
}

/** A `mrow` do celular: nome + chip; sublinha; líquido em negrito + estado. Toque abre o extrato. */
export function LinhaMensalistaMesMobile({ l, quinzena, onAbrir }: Pick<LinhaMensalistaMesProps, "l" | "quinzena" | "onAbrir">) {
  const estado = estadoLinhaMes(l);
  const situacao = situacaoAdiantamentoMes(l);
  const chip = chipRegime(l);
  const adiantado = adiantamentoLinhaMesCentavos(l);
  const partes: string[] = [`${l.dias_registro} ✓`];
  if (l.dias_falta > 0) partes.push(`${l.dias_falta} falta${l.dias_falta === 1 ? "" : "s"}`);
  if (adiantado > 0) partes.push(`− ${formatarBRL(paraReais(adiantado))} adiant.`);
  else if (situacao === "a_gerar") partes.push("adiantamento a gerar");
  if (l.adicionais_centavos > 0) partes.push(`+ ${formatarBRL(paraReais(l.adicionais_centavos))}`);
  if (l.descontos_centavos > 0) partes.push(`− ${formatarBRL(paraReais(l.descontos_centavos))} desc.`);

  return (
    <button
      type="button"
      onClick={() => onAbrir(l)}
      className="w-full bg-card rounded-xl border border-border px-3 py-2.5 flex items-center gap-2 text-left"
      data-testid={`mrow-mes-${l.id}`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 min-w-0">
          <span className="text-sm font-semibold truncate">{l.apelido ?? l.nome}</span>
          <StatusBadge tom={chip.tom} className="text-[10px] font-normal shrink-0">{chip.texto}</StatusBadge>
        </div>
        <p className={`text-[11px] mt-0.5 truncate ${situacao === "a_gerar" ? "text-erro" : "text-muted-foreground"}`}>
          {partes.join(" · ")}
        </p>
      </div>
      <div className="text-right shrink-0">
        <p className="text-sm font-bold tabular-nums">{formatarBRL(liquidoLinhaMes(l))}</p>
        <p className={`text-[10px] ${estado === "fechado" ? "text-sucesso" : estado === "aberto" ? "text-atencao" : "text-muted-foreground"}`}>
          {ESTADO_LINHA[estado].texto}
        </p>
      </div>
      <ChevronRight className="h-4 w-4 text-muted-foreground/60 shrink-0" />
    </button>
  );
}

/** Totais da linha TOTAL (desktop). */
export function totaisDoMes(linhas: LinhaMensalistaMes[]) {
  return linhas.reduce((acc, l) => {
    if (l.era_mensalista && !l.fechamento) return acc;
    acc.salarios += paraCentavos(salarioLinhaMes(l));
    acc.adiantamentos += adiantamentoLinhaMesCentavos(l);
    acc.adicionais += l.adicionais_centavos;
    acc.descontos += l.descontos_centavos;
    acc.liquido += paraCentavos(liquidoLinhaMes(l));
    return acc;
  }, { salarios: 0, adiantamentos: 0, adicionais: 0, descontos: 0, liquido: 0 });
}
